import { Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { NgbDropdown, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { cloneDeep } from 'lodash';
import {
  ButtonComponent,
  FormGroupComponent,
  FormsUtil,
  InputButton,
  InputComponent,
  ModalComponent,
  ModalFooterDirective,
  ModalService,
  ModalSettings,
  ToastService
} from 'mbs-ui-kit';
import { AddNetworkShareModalComponent } from 'mbs-ui-shared@components/add-network-share-modal/add-network-share-modal.component';
import { TreeInModalComponent } from 'mbs-ui-shared@components/tree-in-modal/tree-in-modal.component';
import { NetworkSharePath } from 'mbs-ui-shared@models/NetworkShareModels';
import { NetworkCredentials } from 'mbs-ui-shared@models/backup/network-credential';
import { RmCommandsWrapper } from 'mbs-ui-shared@services/rm-commands.wrapper';
import { networkPathValidator } from 'mbs-ui-shared@utils/validators';
import { noop } from 'rxjs';
import { DataForPath } from 'wizards/helpers/bases/base-for-steps-helper';
import { StepsHelpers } from 'wizards/helpers/steps-helpers';
import { CheckCredentialsResultEnum } from 'wizards/models/what-backup-tree-model';

@UntilDestroy()
@Component({
  selector: 'mbs-network-share',
  templateUrl: './network-share-modal.component.html',
  imports: [
    ButtonComponent,
    FormGroupComponent,
    I18NextPipe,
    InputComponent,
    ModalComponent,
    NgbDropdownModule,
    ReactiveFormsModule,
    ModalFooterDirective
  ]
})
export class NetworkShareModalComponent implements OnInit {
  private i18nPipe = inject(I18NextPipe);
  private modalService = inject(ModalService);
  private rmCommands = inject(RmCommandsWrapper);
  private toast = inject(ToastService);

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;
  @ViewChild('pathDropdown') pathDropdown: NgbDropdown;
  @ViewChild('pathInput', { read: ElementRef }) pathInput: ElementRef<HTMLElement>;

  public readonly elementSelectors = {
    id: {
      btnDropdown: 'network-path-folder-list-1',
      btnOpenTree: 'network-path-folder-tree-1'
    }
  };

  public canUseShareTree = false;
  public credentialsLoading = false;
  public dataForPath: DataForPath;
  public hid: string;
  public loading = false;
  public networkCredentialsList: NetworkCredentials[] = [];
  public networkSharePathArray: NetworkCredentials[] = [];
  public pathDropdownWidth: number = null;
  public testResult: CheckCredentialsResultEnum = CheckCredentialsResultEnum.Unknown;

  public form: FormGroup<NetworkSharePath> = new FormGroup<NetworkSharePath>({
    path: new FormControl('', [
      Validators.required,
      networkPathValidator,
      this.isSelectedValidator.bind(this),
      this.credentialsValidator.bind(this)
    ])
  });

  private readonly separator = ']-[';
  private readonly treeParams = {
    agentType: 'Backup',
    commandType: 'getComputerContent',
    params: { path: '\\\\', offset: 0, limit: 300, isAsync: true, order: 'DisplayNameAsc' }
  };

  ngOnInit(): void {
    if (!this.baseModal.data) return;

    this.canUseShareTree = this.baseModal.data.canUseShareTree;
    this.dataForPath = this.baseModal.data.dataForPath;
    this.hid = this.baseModal.data.hid;
    this.networkSharePathArray = this.baseModal.data.networkSharePathArray;

    if (this.canUseShareTree) this.loadNetworkCredentials();
  }

  credentialsValidator(): ValidationErrors | null {
    return this.testResult !== CheckCredentialsResultEnum.Unknown ? { invalidCredentials: true } : null;
  }

  isSelectedValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const error = { isSelected: { message: this.i18nPipe.transform('shared:validation.existing_folder') } };
    const lowString = control.value.toLowerCase();

    return this.networkSharePathArray.some((el: NetworkCredentials) => el.path.toLowerCase() === lowString) ? error : null;
  }

  resetTestResult(): void {
    if (this.testResult !== CheckCredentialsResultEnum.Unknown) {
      this.testResult = CheckCredentialsResultEnum.Unknown;
      FormsUtil.triggerValidation(this.form);
    }
  }

  pathDropdownOpenChangeHandler(opened: boolean): void {
    if (opened) this.pathDropdownWidth = this.pathInput?.nativeElement?.getBoundingClientRect()?.width;
  }

  pathSelectHandler(path: string): void {
    this.form.get('path').setValue(path);
    this.resetTestResult();
  }

  manageNetworkCredentialsClickHandler(): void {
    const settings = new ModalSettings();
    settings.isCustom = true;
    settings.data = {
      dataForPath: this.dataForPath,
      hid: this.hid,
      needSaveAfterAdd: false,
      networkSharePathArray: Array.from(this.networkSharePathArray)
    };

    this.modalService
      .openCustom(AddNetworkShareModalComponent, settings)
      .then(noop)
      .catch(noop)
      .finally(() => this.loadNetworkCredentials());
  }

  pathNetworkTreeOpen(data?: InputButton): void {
    if (data?.id === this.elementSelectors.id.btnDropdown) return void this.pathDropdown?.toggle();

    const path = this.form.get('path').value;
    const dataForTree = {
      title: this.i18nPipe.transform('wizards:addNetworkShare', { format: 'title' }),
      hid: this.hid,
      resultFolders: path ? [path] : [],
      isEncrypted: false,
      hideFileType: true,
      isNetwork: true,
      oldBackupContentVariant: true,
      onlyOneSelect: true,
      params: cloneDeep(this.treeParams),
      dataForPath: this.dataForPath,
      needHide: (item): boolean => +item.type === 4
    };

    this.modalService
      .openCustom(TreeInModalComponent, { data: dataForTree, collapsing: true })
      .then((result: string[]) => result?.length && this.form.get('path').setValue(result[0]))
      .catch(noop);
  }

  addClickHandler(): void {
    FormsUtil.triggerValidation(this.form);

    if (!this.form.valid) return void (this.testResult = CheckCredentialsResultEnum.Error);

    this.setLoading(true);

    const valuePath = StepsHelpers.getRootPathForShare(this.form.value.path);
    const pathWithEnd = !this.form.value.path.endsWith('\\') ? this.form.value.path + '\\' : this.form.value?.path;
    const path = valuePath && valuePath === pathWithEnd ? pathWithEnd : this.form.value.path;
    const newValue = {
      path: path,
      login: '',
      id: this.networkSharePathArray.length - 1 + this.separator + path.replaceAll(' ', '-')
    };

    this.rmCommands
      .checkNetworkCredentials(this.hid, { path, login: '', password: '' })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.toast.success();
          this.setLoading(false);
          this.networkSharePathArray.push(newValue);
          this.baseModal.save(this.networkSharePathArray);
        },
        error: (error) => {
          if (error?.error?.detail) this.toast.error(error.error.detail);

          this.testResult = CheckCredentialsResultEnum.Error;

          this.setLoading(false);
        }
      });
  }

  cancelClickHandler(): void {
    this.baseModal.close();
  }

  private loadNetworkCredentials(): void {
    this.credentialsLoading = true;

    this.rmCommands
      .getNetworkCredentialsList(this.hid)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (result: NetworkCredentials[]) => {
          this.networkCredentialsList = result || [];
          this.credentialsLoading = false;
        },
        error: () => (this.credentialsLoading = false)
      });
  }

  private setLoading(state: boolean): void {
    this.loading = state;
    this.form[state ? 'disable' : 'enable']();
  }
}
